import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import * as api from '../services/api';

const EventsContext = createContext(null);

export const EventsProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      const data = await api.getEvents();
      setEvents(data || []);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // ─── Events ─────────────────────────
  const createEvent = useCallback(async (formData) => {
    const data = await api.adminCreateEvent(formData);
    setEvents(prev => [...prev, data]);
    return data;
  }, []);

  const updateEvent = useCallback(async (eventId, updates) => {
    const data = await api.adminUpdateEvent(eventId, updates);
    setEvents(prev => prev.map(e => e.id === eventId ? data : e));
    return data;
  }, []);

  const deleteEvent = useCallback(async (eventId) => {
    await api.adminDeleteEvent(eventId);
    setEvents(prev => prev.filter(e => e.id !== eventId));
  }, []);

  const cloneEvent = useCallback(async (eventId) => {
    const data = await api.adminCloneEvent(eventId);
    setEvents(prev => [...prev, data]);
    return data;
  }, []);

  const publishEvent = useCallback(async (eventId) => {
    const data = await api.adminPublishEvent(eventId);
    setEvents(prev => prev.map(e => e.id === eventId ? data : e));
    return data;
  }, []);

  const unpublishEvent = useCallback(async (eventId) => {
    const data = await api.adminUnpublishEvent(eventId);
    setEvents(prev => prev.map(e => e.id === eventId ? data : e));
    return data;
  }, []);

  const fetchAdminEvents = useCallback(async () => {
    try {
      const data = await api.adminListEvents();
      setEvents(data || []);
    } catch (err) {
      console.error('Error fetching admin events:', err);
    }
  }, []);

  // ─── Layout ─────────────────────────
  const getLayout = useCallback((eventId) => api.adminGetLayout(eventId), []);
  const getEventDetail = useCallback((eventId) => api.getEvent(eventId), []);
  const updateLayout = useCallback(async (eventId, layoutData) => {
    await api.adminSaveLayout(eventId, layoutData);
    await fetchEvents();
  }, [fetchEvents]);

  // ─── Reservations ───────────────────
  const getReservations = useCallback((eventId) => api.adminGetReservations(eventId), []);
  const getPendingReservations = useCallback(() => api.adminGetPendingReservations(), []);
  const confirmReservation = useCallback((reservationId, note) =>
    api.adminReservationAction(reservationId, 'confirm', note), []);
  const rejectReservation = useCallback((reservationId, note) =>
    api.adminReservationAction(reservationId, 'reject', note), []);
  const markAttended = useCallback((reservationId) => api.adminMarkAttended(reservationId), []);

  // ─── QR Scanner ─────────────────────
  const findReservationByUuid = useCallback(async (uuid) => {
    try {
      const data = await api.adminScanQr(uuid);
      return data.found ? data : null;
    } catch {
      return null;
    }
  }, []);

  // ─── Payment Methods ────────────────
  const getPaymentMethods = useCallback(() => api.adminGetPaymentMethods(), []);
  const getEventPaymentMethods = useCallback((eventId) => api.getPaymentMethods(eventId), []);
  const createPaymentMethod = useCallback((data) => api.adminCreatePaymentMethod(data), []);
  const updatePaymentMethod = useCallback((pmId, updates) => api.adminUpdatePaymentMethod(pmId, updates), []);
  const deletePaymentMethod = useCallback((pmId) => api.adminDeletePaymentMethod(pmId), []);

  // ─── Client Reservation ─────────────
  const createReservation = useCallback((formData) => api.createReservation(formData), []);

  const value = useMemo(() => ({
    events, loading,
    fetchEvents, fetchAdminEvents,
    createEvent, updateEvent, deleteEvent,
    cloneEvent, publishEvent, unpublishEvent,
    getLayout, getEventDetail, updateLayout,
    getReservations, getPendingReservations,
    confirmReservation, rejectReservation, markAttended,
    findReservationByUuid,
    getPaymentMethods, getEventPaymentMethods,
    createPaymentMethod, updatePaymentMethod, deletePaymentMethod,
    createReservation,
  }), [
    events, loading,
    fetchEvents, fetchAdminEvents,
    createEvent, updateEvent, deleteEvent,
    cloneEvent, publishEvent, unpublishEvent,
    getLayout, getEventDetail, updateLayout,
    getReservations, getPendingReservations,
    confirmReservation, rejectReservation, markAttended,
    findReservationByUuid,
    getPaymentMethods, getEventPaymentMethods,
    createPaymentMethod, updatePaymentMethod, deletePaymentMethod,
    createReservation,
  ]);

  return <EventsContext.Provider value={value}>{children}</EventsContext.Provider>;
};

export const useEvents = () => {
  const context = useContext(EventsContext);
  if (!context) throw new Error('useEvents must be used within EventsProvider');
  return context;
};
